// @flow
import { isAfter, isBefore, startOfDay } from "date-fns";

import { coerceToDate } from "redux/modules/createCabelasClass/parsers/dates";
import { getCapacityError } from "redux/modules/createCabelasClass/parsers/capacity";
import { getPriceError } from "redux/modules/createCabelasClass/parsers/costOptionsForm";
import {
  getClassDuration,
  getDurationError,
} from "redux/modules/createCabelasClass/parsers/duration";
import type { DurationBounds } from "redux/modules/createCabelasClass/parsers/duration";
import {
  MAX_CLASS_DATE_MESSAGE,
  MAX_SCHEDULED_CLASS_DATES,
  getMaxClassDate,
} from "redux/modules/createCabelasClass/constants";
import type {
  CabelasClassFormValues,
  ScheduledClassDate,
} from "redux/modules/createCabelasClass/parsers/form";

export type ClassDateErrors = {
  capacity?: string,
  classDate?: string,
  startTime?: string,
  endTime?: string,
};

export type ScheduleFormErrors = {
  eventContentId?: string,
  locationId?: string,
  hostIds?: string,
  capacity?: string,
  price?: string,
  timezone?: string,
  costOption?: string,
  templateId?: string,
  pricingError?: string,
  classDates?: Array<ClassDateErrors> | string, // string = no rows; array = per-row
};

const DUPLICATE_MESSAGE = "Duplicate date and start time";
const ROW_FIELD_LABELS: { [string]: string } = {
  capacity: "capacity",
  classDate: "date",
  startTime: "start time",
  endTime: "end time",
};

const getScheduleKey = (row: ScheduledClassDate): ?string => {
  const date = coerceToDate(row.classDate);
  const start = coerceToDate(row.startTime);

  if (!date || !start) {
    return null;
  }

  return `${startOfDay(
    date
  ).getTime()}-${start.getHours()}:${start.getMinutes()}`;
};

export const validateClassDates = (
  classDates: Array<ScheduledClassDate>,
  durationBounds?: DurationBounds
): Array<ClassDateErrors> => {
  const rowErrors: Array<ClassDateErrors> = classDates.map(
    (): ClassDateErrors => ({})
  );
  const firstIndexByKey: { [string]: number } = {};

  classDates.forEach((row: ScheduledClassDate, index: number) => {
    const errors = rowErrors[index];
    const parsedDate = coerceToDate(row.classDate);

    if (!parsedDate) {
      errors.classDate = "Required";
    } else if (isBefore(startOfDay(parsedDate), startOfDay(new Date()))) {
      errors.classDate = "Must be today or in the future";
    } else if (isAfter(startOfDay(parsedDate), startOfDay(getMaxClassDate()))) {
      errors.classDate = MAX_CLASS_DATE_MESSAGE;
    }

    if (!coerceToDate(row.startTime)) {
      errors.startTime = "Required";
    }

    if (!coerceToDate(row.endTime)) {
      errors.endTime = "Required";
    }

    // Wall-clock only; pickers carry 1899 sentinel dates.
    const startTime = coerceToDate(row.startTime);
    const endTime = coerceToDate(row.endTime);

    const durationError = getDurationError(
      startTime && endTime ? getClassDuration(startTime, endTime) : null,
      durationBounds
    );

    if (durationError) {
      errors.startTime = errors.startTime || durationError;
      errors.endTime = errors.endTime || durationError;
    }

    const scheduleKey = getScheduleKey(row);

    if (scheduleKey) {
      const firstIndex = firstIndexByKey[scheduleKey];

      if (firstIndex === undefined) {
        firstIndexByKey[scheduleKey] = index;
      } else {
        errors.classDate = errors.classDate || DUPLICATE_MESSAGE;
        rowErrors[firstIndex].classDate =
          rowErrors[firstIndex].classDate || DUPLICATE_MESSAGE;
      }
    }
  });

  return rowErrors;
};

const validate = (values: CabelasClassFormValues): ScheduleFormErrors => {
  const errors: ScheduleFormErrors = {};
  const classDates = values.classDates || [];

  if (!values.eventContentId) {
    errors.eventContentId = "Required";
  }

  if (values.locationId && !values.hostIds?.length) {
    errors.hostIds = "No assigned instructors for the selected location";
  }

  if (!values.locationId) {
    errors.locationId = "Required";
  }

  if (!classDates.some((row: ScheduledClassDate): boolean => !!row.classDate)) {
    const capacityError = getCapacityError(
      values.capacity,
      values.maxCapacity,
      false
    );

    if (capacityError) {
      errors.capacity = capacityError;
    }
  }

  if (!values.timezone) {
    errors.timezone = "Required";
  }

  if (values.eventContentId && !values.costOption) {
    errors.costOption = "The selected class has no available pricing";
  }

  if (values.eventContentId && !values.templateId) {
    errors.templateId = "No matching pricing template for the selected class";
  }

  if (values.eventContentId && values.pricingError) {
    errors.pricingError = values.pricingError;
  }

  const priceError = getPriceError(values.price);
  if (values.eventContentId && priceError) {
    errors.price = priceError;
  }

  if (!classDates.length) {
    errors.classDates = "Add at least one class date";
    return errors;
  }

  const rowErrors = validateClassDates(classDates, values);

  rowErrors.forEach((rowError: ClassDateErrors, index: number) => {
    const capacityError = getCapacityError(
      classDates[index].capacity,
      values.maxCapacity
    );

    if (capacityError) {
      rowError.capacity = capacityError;
    }

    if (index >= MAX_SCHEDULED_CLASS_DATES) {
      rowError.classDate =
        rowError.classDate ||
        `Schedule up to ${MAX_SCHEDULED_CLASS_DATES} classes at a time`;
    }
  });

  const hasRowErrors = rowErrors.some(
    (rowError: ClassDateErrors): boolean => !!Object.keys(rowError).length
  );

  if (hasRowErrors) {
    errors.classDates = rowErrors;
  }

  return errors;
};

// Hidden/untouched field errors never render; list them for the disabled Save.
export const getOutstandingRequirements = (
  errors: ScheduleFormErrors
): Array<string> => {
  const items = [];

  if (errors.eventContentId) {
    items.push("a class");
  }

  if (errors.locationId) {
    items.push("a location");
  }

  if (errors.hostIds) {
    items.push("assigned instructors for the selected location");
  }

  if (errors.costOption || errors.templateId || errors.pricingError) {
    items.push("pricing for the selected class");
  }

  if (errors.capacity) {
    items.push(`default capacity (${String(errors.capacity).toLowerCase()})`);
  }

  if (
    errors.price &&
    !errors.pricingError &&
    !errors.costOption &&
    !errors.templateId
  ) {
    items.push(`price (${String(errors.price).toLowerCase()})`);
  }

  if (errors.timezone && !errors.locationId) {
    items.push("a time zone for the selected location");
  }

  if (typeof errors.classDates === "string") {
    items.push(String(errors.classDates).toLowerCase());
  } else if (Array.isArray(errors.classDates)) {
    errors.classDates.forEach((rowError: ClassDateErrors, index: number) => {
      const [firstKey] = Object.keys(rowError || {});

      if (!firstKey) {
        return;
      }

      const message = String(rowError[firstKey]);
      const detail =
        message === "Required"
          ? `${ROW_FIELD_LABELS[firstKey] || firstKey} required`
          : `${
              firstKey === "capacity" ? "capacity " : ""
            }${message.toLowerCase()}`;

      items.push(`Class ${index + 1}: ${detail}`);
    });
  }

  return items;
};

export default validate;
